import InputUpload from '@app/components/atoms/Images/InputUpload'
import LabelField from '@app/components/atoms/LabelField/LabelField'
import Section from '@app/components/atoms/Section/Section'
import MerchantCreateSchema from '@app/modules/Admin/Merchant/MerchantCreate/MerchantCreateSchema'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Inertia } from '@inertiajs/inertia'
import { InertiaLink } from '@inertiajs/inertia-react'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { TypographyCustomizer, DivCustomizer } from '../MerchantStyle'
import POSSection from './POSSection'

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />
const checkedIcon = <CheckBoxIcon fontSize="small" />

const MerchantCreate = ({ categories, status, pos }) => {
  const { t } = useTranslation()
  const [disabled, setDisabled] = useState(false)

  const initialValues = {
    name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    logo: [],
    status: 'active',
    categories: [],
    pos: [],
  }

  Inertia.on('finish', () => setDisabled(false))

  const onSubmit = (data) => {
    setDisabled(true)
    Inertia.post(route('admin.merchants.store'), data, {})
  }
  const onError = (errors, e) => {}

  const {
    register,
    trigger,
    control,
    handleSubmit,
    getValues,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: initialValues,
    resolver: yupResolver(MerchantCreateSchema),
  })

  return (
    <form onSubmit={handleSubmit(onSubmit, onError)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Section title={t('MERCHANT.CREATE_TITLE')}>
            <Grid container>
              <Grid item lg={7} md={12} sm={12} xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.NAME')}>
                      <TextField
                        fullWidth
                        id="name"
                        name="name"
                        size="small"
                        variant="outlined"
                        {...register('name')}
                        error={errors.name ? true : false}
                        helperText={errors.name?.message}
                        onFocus={() => trigger('name')}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.EMAIL')}>
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        size="small"
                        type="email"
                        variant="outlined"
                        {...register('email')}
                        error={errors.email ? true : false}
                        helperText={errors.email?.message}
                        onFocus={() => trigger('email')}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.PHONE_NUMBER')}>
                      <TextField
                        fullWidth
                        id="phone"
                        name="phone"
                        size="small"
                        variant="outlined"
                        {...register('phone')}
                        error={errors.phone ? true : false}
                        helperText={errors.phone?.message}
                        onFocus={() => trigger('phone')}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.ADDRESS')}>
                      <TextField
                        fullWidth
                        id="address"
                        name="address"
                        size="small"
                        variant="outlined"
                        {...register('address')}
                        error={errors.address ? true : false}
                        helperText={errors.address?.message}
                        onFocus={() => trigger('address')}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.CATEGORY')}>
                      <Controller
                        control={control}
                        name="categories"
                        render={({ field }) => (
                          <Autocomplete
                            disableCloseOnSelect
                            {...field}
                            multiple
                            getOptionLabel={(option) => option.name}
                            id="checkboxes-tags-demo"
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            options={categories}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                error={!!errors.categories && !getValues('categories').length}
                                helperText={
                                  errors.categories && !getValues('categories').length ? errors.categories?.message : ''
                                }
                                onFocus={() => trigger('categories')}
                              />
                            )}
                            renderOption={(props, option, { selected }) => (
                              <li {...props}>
                                <Checkbox
                                  checked={selected}
                                  checkedIcon={checkedIcon}
                                  icon={icon}
                                  style={{ marginRight: 8 }}
                                />
                                {option.name}
                              </li>
                            )}
                            size="small"
                            onChange={(e, value) => setValue('categories', value)}
                          />
                        )}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required title={t('MERCHANT.STATUS')}>
                      <Controller
                        control={control}
                        name="status"
                        render={() => (
                          <TextField
                            fullWidth
                            select
                            name="status"
                            size="small"
                            value={getValues('status')}
                            variant="outlined"
                            {...register('status')}
                            error={errors.status ? true : false}
                            helperText={errors.status?.message}
                          >
                            {status.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {t(option.name)}
                              </MenuItem>
                            ))}
                          </TextField>
                        )}
                      />
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField required recommend={t('VOUCHER.SLIDER_RECOMMEND')} title={t('MERCHANT.LOGO')}>
                      <Controller
                        control={control}
                        name="logo"
                        render={({ field }) => (
                          <InputUpload
                            {...field}
                            buttonName={t('MERCHANT.UPLOAD_LOGO')}
                            clearErrors={clearErrors}
                            handleImageChange={(files) => setValue('logo', files)}
                            id="logo"
                            name="logo"
                            ref={field.ref}
                            setError={setError}
                            title={t('MERCHANT.UPLOAD_LOGO')}
                            value={getValues('logo')}
                          />
                        )}
                      />
                      {errors.logo && (
                        <TypographyCustomizer variant="caption">{errors.logo?.message}</TypographyCustomizer>
                      )}
                    </LabelField>
                  </Grid>

                  <Grid item xs={12}>
                    <LabelField title={t('MERCHANT.DESCRIPTION')}>
                      <DivCustomizer>
                        <CKEditor
                          data={getValues('description')}
                          editor={ClassicEditor}
                          id="description"
                          name="description"
                          onChange={(value, editor) => setValue('description', editor.getData())}
                        />
                      </DivCustomizer>
                    </LabelField>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs={12}>
          <Section title={t('VOUCHER.POS')}>
            <Grid container>
              <Grid item lg={9} md={12} sm={12} xs={12}>
                <POSSection
                  control={control}
                  errors={errors}
                  getValues={getValues}
                  pos={pos}
                  register={register}
                  setValue={setValue}
                />
              </Grid>
            </Grid>
          </Section>
        </Grid>

        <Grid item xs="auto">
          <Button color="primary" disabled={disabled} type="submit" variant="contained">
            {t('MERCHANT.SAVE')}
          </Button>
        </Grid>

        <Grid item xs="auto">
          <Button color="inherit" component={InertiaLink} href={route('admin.merchants.index')} variant="contained">
            {t('MERCHANT.CANCEL')}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default MerchantCreate
